import React, { HTMLProps, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { InputField } from '@design-system/inputs';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Form } from './components/Form';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { Button } from '@design-system';

export type NameEmailFormFields = {
  name: string;
  email: string;
};

export interface INameEmailFormProps
  extends Omit<HTMLProps<HTMLElement>, 'onSubmit'> {
  className?: string;
  onSubmit?: (v: NameEmailFormFields, reset: any) => void;
  primaryColor: string;
  hasButton: boolean;
  onFormChange?: (data: any) => void;
  defaultValues?: NameEmailFormFields;
}

export const NameEmailForm: React.FC<INameEmailFormProps> = (
  props,
): JSX.Element => {
  const {
    className,
    primaryColor,
    onSubmit,
    hasButton = true,
    onFormChange,
    defaultValues,
    ...rest
  } = props;
  const { t } = useTranslation();

  // Define schema inside component to avoid initialization timing issues
  const schema = React.useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .required(translations.ERRORS.FORM_NAME_REQUIRED)
          .max(50, translations.ERRORS.FORM_NAME_MAX_50),
        email: Yup.string()
          .email(translations.ERRORS.VALID_EMAIL)
          .required(translations.ERRORS.REQUIRED_EMAIL)
          .min(6, translations.ERRORS.VALID_EMAIL_CHARACTERS),
      }),
    [],
  );

  const { formState, control, handleSubmit, watch, reset } =
    useForm<NameEmailFormFields>({
      defaultValues: defaultValues ?? { name: '', email: '' },
      mode: 'onTouched',
      resolver: yupResolver(schema),
    });
  const { isValid, isSubmitting } = formState;
  const isDisabledRef = useRef<boolean | null>(null);

  React.useEffect(() => {
    isDisabledRef.current = isSubmitting || !isValid;

    const subscription = watch((value) => {
      if (onFormChange && value) {
        onFormChange({
          ...value,
          isDisabledRef: isDisabledRef.current,
          isDisabled: isSubmitting || !isValid,
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, isValid, isSubmitting, onFormChange]);

  return (
    <Form
      className={className}
      onSubmit={handleSubmit((values) =>
        onSubmit ? onSubmit(values, reset) : null,
      )}
      {...rest}>
      <InputField
        className="mb-4 w-full"
        control={control}
        name="name"
        placeholder={t(translations.INPUTS.NAME)}
        size="md"
        primaryColor={primaryColor}
        type="text"
        autoComplete="off"
      />
      <InputField
        className="mb-4 w-full"
        control={control}
        name="email"
        placeholder={t(translations.COMMON.EMAIL)}
        size="md"
        primaryColor={primaryColor}
        type="text"
        autoComplete="off"
      />
      {hasButton && (
        <div className="flex w-full items-center justify-center">
          <Button
            color={primaryColor}
            className="max-w mb-2 mr-8 px-8 text-white"
            isDisabled={formState?.isSubmitting || !formState?.isValid}
            type="submit"
            variant="solid"
            isLoading={formState?.isSubmitting}
            size="md">
            {t(translations.COMMON.ADD)}
          </Button>
        </div>
      )}
    </Form>
  );
};
